import { useState } from 'react';
import { useAlertRules, useCreateAlertRule } from '../api/intelligence';
import type { AlertRule } from '../api/intelligence';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400',
  high: 'bg-orange-500/20 text-orange-400',
  medium: 'bg-amber-500/20 text-amber-400',
  low: 'bg-green-500/20 text-green-400',
};

const RULE_TYPE_LABELS: Record<string, string> = {
  risk_threshold: 'Seuil de risque',
  new_relationship: 'Nouvelle relation',
  darkweb_mention: 'Mention darkweb',
  entity_change: 'Modification entite',
  leak_detected: 'Fuite detectee',
};

export default function AlertRulesPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const { data, isLoading, refetch } = useAlertRules(filterActive);
  const createMutation = useCreateAlertRule();

  // Create form state
  const [name, setName] = useState('');
  const [ruleType, setRuleType] = useState('risk_threshold');
  const [severity, setSeverity] = useState('medium');
  const [conditionsJson, setConditionsJson] = useState('{"min_score": 0.8}');

  const handleCreate = async () => {
    try {
      const conditions = JSON.parse(conditionsJson);
      await createMutation.mutateAsync({
        name,
        description: '',
        rule_type: ruleType,
        conditions,
        severity,
        notify_email: null,
        active: true,
      });
      setShowCreate(false);
      setName('');
      refetch();
    } catch {
      // parse or API error
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Regles d'alertes</h1>
          <p className="text-sm text-gray-500 mt-1">Alertes automatiques basees sur des conditions</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterActive === undefined ? 'all' : filterActive ? 'active' : 'inactive'}
            onChange={(e) => {
              if (e.target.value === 'all') setFilterActive(undefined);
              else setFilterActive(e.target.value === 'active');
            }}
            className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
          >
            <option value="all">Toutes</option>
            <option value="active">Actives</option>
            <option value="inactive">Inactives</option>
          </select>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
          >
            + Nouvelle regle
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="glass-panel p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-200">Creer une regle</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nom</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
                placeholder="Ex: Entites haut risque"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select
                value={ruleType}
                onChange={(e) => setRuleType(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
              >
                {Object.entries(RULE_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Severite</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Conditions (JSON)</label>
              <input
                value={conditionsJson}
                onChange={(e) => setConditionsJson(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300 font-mono"
                placeholder='{"min_score": 0.8}'
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200">
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={!name || createMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-cyber-cyan text-surface-primary rounded-lg hover:bg-cyan-400 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creation...' : 'Creer'}
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
        </div>
      )}

      {/* Rules table */}
      {data && data.items.length > 0 && (
        <div className="glass-panel overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Severite</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Declenchements</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Dernier</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((rule: AlertRule) => (
                <tr key={rule.id} className="border-b border-border-subtle/50 hover:bg-surface-tertiary/30 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-200">{rule.name}</p>
                    {rule.description && <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-400">{RULE_TYPE_LABELS[rule.rule_type] || rule.rule_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${SEVERITY_COLORS[rule.severity] || 'bg-gray-500/20 text-gray-400'}`}>
                      {rule.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${rule.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {rule.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 font-mono">{rule.trigger_count}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {rule.last_triggered_at ? new Date(rule.last_triggered_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && data && data.items.length === 0 && (
        <p className="text-center text-gray-500 py-12">Aucune regle d'alerte configuree</p>
      )}
    </div>
  );
}
